import type RouterService from '@ember/routing/router-service';
import type Transition from '@ember/routing/transition';
import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type StateService from 'garaje/services/state';

export type SensitiveState = 'visible' | 'hidden';

/**
 * The masking policy shared by every sensitive-field surface: a `hidden` field
 * is always masked, and a `visible` field is masked until it is revealed. The
 * reveal source differs per surface — page-level service state for list columns
 * ({@link SensitiveFieldsVisibilityService#isRevealed}) versus local component
 * state for detail-page inputs — so it is passed in rather than read here.
 */
export function isSensitiveFieldMasked(state: SensitiveState | undefined, revealed: boolean): boolean {
  if (state === 'hidden') return true;
  return state === 'visible' && !revealed;
}

export default class SensitiveFieldsVisibilityService extends Service {
  @service declare router: RouterService;
  @service declare state: StateService;

  @tracked revealedFields: Record<string, boolean> = {};

  #lastRouteName?: string;
  #lastLocationId?: string;

  constructor(...args: ConstructorParameters<typeof Service>) {
    super(...args);
    this.#lastRouteName = this.router.currentRouteName ?? undefined;
    this.#lastLocationId = this.state.currentLocation?.id;
    this.router.on('routeDidChange', this.handleRouteChange);
  }

  willDestroy(): void {
    this.router.off('routeDidChange', this.handleRouteChange);
    super.willDestroy();
  }

  isRevealed(fieldName: string): boolean {
    return Boolean(this.revealedFields[fieldName]);
  }

  toggle(fieldName: string): void {
    this.revealedFields = { ...this.revealedFields, [fieldName]: !this.isRevealed(fieldName) };
  }

  reset(): void {
    if (Object.keys(this.revealedFields).length > 0) {
      this.revealedFields = {};
    }
  }

  /**
   * Re-mask every revealed sensitive column whenever the user navigates to a
   * different route or switches location, so revealed values never carry over
   * into a new view. Query-param-only changes (sort/filter/search) keep both
   * the route name and location the same, so they preserve the reveal state.
   */
  handleRouteChange = (transition: Transition): void => {
    const routeName = transition.to?.name;
    const locationId = this.state.currentLocation?.id;

    if (routeName !== this.#lastRouteName || locationId !== this.#lastLocationId) {
      this.reset();
    }

    this.#lastRouteName = routeName;
    this.#lastLocationId = locationId;
  };
}

declare module '@ember/service' {
  interface Registry {
    'sensitive-fields-visibility': SensitiveFieldsVisibilityService;
  }
}
